import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getVideoTestimonials } from "@/lib/api";
import { usePageTracking } from "@/hooks/useAuditLog";
import { 
  Play, 
  Search, 
  Filter, 
  X, 
  Video, 
  Building2, 
  User, 
  Quote,
  ArrowLeft,
  Loader2,
  Grid3X3,
  List
} from "lucide-react";
import { toast } from "sonner";

const getYouTubeVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : '';
};

const getYouTubeThumbnail = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

export default function VideoTestimonialsPage() {
  usePageTracking('Video Testimonials Gallery');
  
  const [testimonials, setTestimonials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [playingVideo, setPlayingVideo] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const res = await getVideoTestimonials();
        setTestimonials(res.data.testimonials || []);
        setCategories(res.data.categories || []);
      } catch (error) {
        toast.error("Failed to load video testimonials");
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  // Filter testimonials based on search and category
  const filteredTestimonials = testimonials.filter(t => {
    const matchesSearch = searchQuery === "" || 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.quote.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.vertical_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || t.vertical_name === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handlePlayVideo = (testimonial) => {
    setPlayingVideo(testimonial);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#0066CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <main className="pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#0066CC] via-[#0052A3] to-[#003366] py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-white/80 hover:text-white text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Video className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                  Video Testimonials
                </h1>
                <p className="text-white/70 text-lg mt-1">
                  See what our clients have to say about their digital transformation journey
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 mt-6">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {testimonials.length} Video{testimonials.length !== 1 ? 's' : ''}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {categories.length} Industri{categories.length !== 1 ? 'es' : 'y'}
              </Badge>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="bg-white border-b border-slate-200 sticky top-16 md:top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by name, company, or industry..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="testimonial-search-input"
                />
              </div>
              
              <div className="flex items-center gap-3 w-full md:w-auto">
                {/* Category Filter */}
                <div className="flex items-center gap-2 flex-wrap flex-1 md:flex-none">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#0066CC]/20 focus:border-[#0066CC]"
                    data-testid="category-filter"
                  >
                    <option value="all">All Industries</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                {/* View Mode Toggle */}
                <div className="flex border border-slate-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 ${viewMode === "grid" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="grid-view-btn"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 ${viewMode === "list" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="list-view-btn"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Grid/List */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredTestimonials.length === 0 ? (
              <div className="text-center py-16">
                <Video className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Video Testimonials Found</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  {searchQuery || selectedCategory !== "all" 
                    ? "Try adjusting your search or filter criteria"
                    : "Video testimonials will appear here once they are added to verticals"
                  }
                </p>
                {(searchQuery || selectedCategory !== "all") && (
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => { setSearchQuery(""); setSelectedCategory("all"); }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : viewMode === "grid" ? (
              /* Grid View */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
                {filteredTestimonials.map((testimonial) => (
                  <div 
                    key={testimonial.id}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#0066CC]/30 transition-all duration-300"
                    data-testid={`testimonial-card-${testimonial.id}`}
                  >
                    {/* Video Thumbnail */}
                    <div className="relative aspect-video bg-slate-100">
                      <img
                        src={getYouTubeThumbnail(testimonial.video_url)}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/480x270?text=Video"; }}
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <button
                          onClick={() => handlePlayVideo(testimonial)}
                          className="w-16 h-16 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform"
                          data-testid={`play-btn-${testimonial.id}`}
                        >
                          <Play className="h-7 w-7 text-[#0066CC] ml-1" fill="currentColor" />
                        </button>
                      </div>
                      <Badge className="absolute top-3 left-3 bg-[#0066CC] text-white">
                        {testimonial.vertical_name}
                      </Badge>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {testimonial.image_url ? (
                            <img 
                              src={testimonial.image_url} 
                              alt={testimonial.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User className="h-5 w-5 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{testimonial.name}</h3>
                          <p className="text-sm text-slate-500 truncate">
                            {testimonial.role}{testimonial.role && testimonial.company ? " at " : ""}{testimonial.company}
                          </p>
                        </div>
                      </div>
                      
                      {testimonial.quote && (
                        <p className="mt-3 text-sm text-slate-600 line-clamp-2 italic">
                          &ldquo;{testimonial.quote}&rdquo;
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-4" data-testid="testimonials-list">
                {filteredTestimonials.map((testimonial) => (
                  <div 
                    key={testimonial.id}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#0066CC]/30 transition-all duration-300 flex flex-col md:flex-row"
                    data-testid={`testimonial-list-item-${testimonial.id}`}
                  >
                    {/* Video Thumbnail */}
                    <div className="relative w-full md:w-72 aspect-video md:aspect-auto md:h-48 bg-slate-100 flex-shrink-0">
                      <img
                        src={getYouTubeThumbnail(testimonial.video_url)}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/480x270?text=Video"; }}
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        <button
                          onClick={() => handlePlayVideo(testimonial)}
                          className="w-14 h-14 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform"
                        >
                          <Play className="h-6 w-6 text-[#0066CC] ml-1" fill="currentColor" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-5 flex-1 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-[#0066CC] border-[#0066CC]/30">
                          <Building2 className="h-3 w-3 mr-1" />
                          {testimonial.vertical_name}
                        </Badge>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          {testimonial.image_url ? (
                            <img 
                              src={testimonial.image_url} 
                              alt={testimonial.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <User className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-slate-900">{testimonial.name}</h3>
                          <p className="text-sm text-slate-500">
                            {testimonial.role}{testimonial.role && testimonial.company ? " at " : ""}{testimonial.company}
                          </p>
                        </div>
                      </div>
                      
                      {testimonial.quote && (
                        <div className="mt-3 flex items-start gap-2">
                          <Quote className="h-4 w-4 text-[#0066CC] flex-shrink-0 mt-1" />
                          <p className="text-slate-600 italic line-clamp-2">
                            {testimonial.quote}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Results count */}
            {filteredTestimonials.length > 0 && (
              <p className="text-center text-slate-500 text-sm mt-8">
                Showing {filteredTestimonials.length} of {testimonials.length} video testimonial{testimonials.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </section>
      </main>

      {/* Video Player Modal */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="video-modal">
          <div className="relative">
            <button
              onClick={() => setPlayingVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-white/80 transition-colors z-10"
            >
              <X className="h-8 w-8" />
            </button>
            
            {playingVideo && (
              <div className="aspect-video bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(playingVideo.video_url)}?autoplay=1&rel=0`}
                  title={playingVideo.name}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
          
          {playingVideo && (
            <div className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  {playingVideo.image_url ? (
                    <img 
                      src={playingVideo.image_url} 
                      alt={playingVideo.name}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{playingVideo.name}</h3>
                  <p className="text-sm text-slate-500">
                    {playingVideo.role}{playingVideo.role && playingVideo.company ? " at " : ""}{playingVideo.company}
                  </p>
                </div>
                <Badge className="ml-auto bg-[#0066CC]">{playingVideo.vertical_name}</Badge>
              </div>
              
              {playingVideo.quote && (
                <p className="mt-3 text-slate-600 italic border-l-2 border-[#0066CC] pl-3">
                  &ldquo;{playingVideo.quote}&rdquo;
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
